import React, {useMemo} from 'react';
import striptags from 'striptags';
import styled from 'styled-components';
import {createHelpCenterArticleUrl} from '../../../pages/HelpCenter/routes';
import {
  HelpCenterArticle,
  HelpCenterSection,
} from '../../../pages/HelpCenter/types';
import Icon, {IconSize} from '../../Icon/Icon';
import {GenericIconName} from '../../Icon/IconName';
import ClientSideLink from '../../Navigation/ClientSideLink/ClientSideLink';
import {
  complementaryColor,
  darkGrayColor,
  primaryColor,
} from '../../constants/colors';
import {
  mediaMaxTablet,
  mediaMinTablet,
  minTabletOnlyCss,
  mobileOnlyCss,
} from '../../constants/customMedia';
import {gridGapPx, mobileBorderRadius} from '../../constants/misc';
import {fsS, fwBold, fwMediumBold} from '../../constants/typography';

interface Props {
  article: HelpCenterArticle;
  section: HelpCenterSection;
  translations: {
    read_more: string;
  };
  className?: string;
}
const ArticleTitle = styled.span`
  display: flex;
  font-size: ${fsS}px;
  position: relative;
  box-sizing: border-box;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  ${fwMediumBold}
  line-height: 20px;

  @media ${mediaMinTablet} {
    margin-bottom: 15px;
    ${fwBold};
  }
`;

const ArticleDescription = styled.span`
  display: block;
  display: -webkit-box;
  text-overflow: ellipsis;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  color: ${darkGrayColor};
  font-size: 14px;
  ${minTabletOnlyCss}
  @media ${mediaMinTablet} {
    margin-bottom: 12px;
    -webkit-line-clamp: 3;
  }
`;

const ReadMore = styled.span`
  display: inline-block;
  ${minTabletOnlyCss}
  ${fwMediumBold}
  position: relative;

  &:after {
    content: '';
    position: absolute;
    bottom: -4px;
    height: 1.5px;
    line-height: 0;
    width: 0;
    left: 0;
    background: ${primaryColor};
    transition: 0.3s all;
  }
`;

const StyledArrowIcon = styled(Icon)`
  ${mobileOnlyCss};
  fill: ${darkGrayColor};
`;

const StyledQuestionIcon = styled(Icon)`
  ${minTabletOnlyCss};
  position: absolute;
  right: 100%;
  top: 2px;
  margin-right: 5px;
`;

const StyledClientSideLink = styled(ClientSideLink)`
  display: block;
  text-decoration: none;
  color: ${primaryColor};
  padding: 0 ${gridGapPx};
  margin-bottom: 30px;

  @media ${mediaMaxTablet} {
    display: flex;
    line-height: 20px;
    border-radius: ${mobileBorderRadius};
    background: ${complementaryColor};
    align-items: center;
    min-height: 60px;
    box-sizing: border-box;
    padding: 8px ${gridGapPx};
    margin: 0 0 8px;
    box-shadow: 2px 8px 20px 0 rgba(0, 0, 0, 0.05),
      0 1px 2px 0 rgba(0, 0, 0, 0.05);
  }

  @media ${mediaMinTablet} {
    padding: 0 ${gridGapPx};
    box-sizing: border-box;
  }

  &:hover ${ReadMore} {
    &:after {
      width: 100%;
    }
  }
`;

const HelpdeskSectionListItem: React.FC<Props> = ({
  article,
  section,
  translations,
  className,
}) => {
  const bodyWithoutTags = useMemo(
    () => striptags(article.body),
    [article.body],
  );
  const description = useMemo(
    () => bodyWithoutTags.slice(0, 180),
    [bodyWithoutTags],
  );

  return (
    <StyledClientSideLink
      href={createHelpCenterArticleUrl(section, article)}
      className={className}
      data-testid="HelpCenterSectionPageArticleContainer"
    >
      <ArticleTitle data-testid="HelpCenterSectionPageArticleHeadline">
        <StyledQuestionIcon
          data-test-icon="HelpCenterQuestionIcon"
          size={IconSize.XS}
          asset={GenericIconName.IcQuestionFillAlt}
        />
        {article.name}
        <StyledArrowIcon
          size={IconSize.S}
          asset={GenericIconName.IcArrowSmallRight}
        />
      </ArticleTitle>
      <ArticleDescription data-testid="HelpCenterSectionPageArticleDescription">
        {description}
      </ArticleDescription>
      <ReadMore data-testid="HelpCenterSectionPageArticleLink">
        {translations.read_more}
      </ReadMore>
    </StyledClientSideLink>
  );
};

export default HelpdeskSectionListItem;
